import { motion } from "framer-motion";
import { Check, CheckCircle2, TrendingUp, Moon } from "lucide-react";

const chipCls = "absolute flex items-center gap-2 border border-line bg-base px-4 py-2 backdrop-blur-sm";
const chipText = "text-xs uppercase tracking-[0.15em] text-mut";

const sopItems = [
  "Vendor follow-ups done",
  "Inventory sheet updated",
  "Customer queries cleared",
  "Sales report compiled",
  "Payment reminders sent",
];

export const ChecklistVisual = () => (
  <div className="visual-grid relative flex h-full w-full items-center justify-center p-5">
    <div className="w-full max-w-xs rounded-xl border border-line bg-base p-5 shadow-xl shadow-black/20">
      <div className="flex items-center justify-between border-b border-line pb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-mut">Daily Ops Report</span>
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-pop">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-myrtle" />
          9:00 AM
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {sopItems.map((t, i) => (
          <motion.div
            key={t}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
            className="flex items-center gap-3 text-xs text-mut"
          >
            <motion.span
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.12, type: "spring", stiffness: 300, damping: 16 }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-myrtle text-white"
            >
              <Check size={12} />
            </motion.span>
            {t}
          </motion.div>
        ))}
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-faint">
          <span>SOPs Complete</span>
          <span>7/9</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "78%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-full bg-myrtle"
          />
        </div>
      </div>
    </div>
    <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3.4, ease: "easeInOut" }} className={`${chipCls} right-4 top-5 max-sm:hidden`}>
      <CheckCircle2 size={14} className="text-pop" />
      <span className={chipText}>Report Delivered</span>
    </motion.div>
  </div>
);

const bubbles = [
  { text: "Need 50 units of SKU-22", mine: false },
  { text: "Catalog shared", mine: true },
  { text: "Order #1024 confirmed", mine: true },
  { text: "Payment received — ₹18,400", mine: false },
  { text: "Tracking link sent", mine: true },
];

export const PhoneVisual = () => (
  <div className="visual-grid relative flex h-full w-full items-center justify-center">
    <div className="relative h-[88%] w-[62%] max-w-[240px] overflow-hidden rounded-[28px] border border-line bg-base">
      <div className="relative z-20 flex items-center gap-2 border-b border-line bg-myrtle/15 px-4 py-3">
        <span className="h-2 w-2 animate-pulse rounded-full bg-myrtle" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-mut">Allcov Business</span>
      </div>
      <div className="relative h-[calc(100%-45px)] overflow-hidden">
        <div className="animate-chatslide px-3 pt-3">
          {[...bubbles, ...bubbles].map((b, i) => (
            <div
              key={i}
              className={`mb-3 max-w-[85%] px-3 py-2 text-[11px] leading-snug ${
                b.mine
                  ? "ml-auto rounded-l-xl rounded-tr-xl bg-myrtle text-white"
                  : "rounded-r-xl rounded-tl-xl border border-line bg-surface text-mut"
              }`}
            >
              {b.text}
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-10" style={{ background: "linear-gradient(var(--bg), transparent)" }} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10" style={{ background: "linear-gradient(transparent, var(--bg))" }} />
      </div>
    </div>
    <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }} className={`${chipCls} right-4 top-6 max-sm:hidden`}>
      <CheckCircle2 size={14} className="text-pop" />
      <span className={chipText}>Order #1024</span>
    </motion.div>
  </div>
);

const nodes = [
  { cx: 70, cy: 150, l: "TRIGGER" },
  { cx: 218, cy: 84, l: "AI CORE" },
  { cx: 218, cy: 216, l: "AI CORE" },
  { cx: 328, cy: 150, l: "SENT" },
];

export const WorkflowVisual = () => (
  <div className="visual-grid relative flex h-full w-full items-center justify-center">
    <svg viewBox="0 0 400 300" className="w-full max-w-md" fill="none" aria-hidden="true">
      <path d="M92 150 C 140 150 150 84 196 84" stroke="var(--pop)" strokeWidth="1.5" strokeDasharray="5 7" className="animate-dash" opacity="0.7" />
      <path d="M92 150 C 140 150 150 216 196 216" stroke="var(--pop)" strokeWidth="1.5" strokeDasharray="5 7" className="animate-dash" opacity="0.7" />
      <path d="M240 84 C 276 84 278 150 306 150" stroke="var(--pop)" strokeWidth="1.5" strokeDasharray="5 7" className="animate-dash" opacity="0.7" />
      <path d="M240 216 C 276 216 278 150 306 150" stroke="var(--pop)" strokeWidth="1.5" strokeDasharray="5 7" className="animate-dash" opacity="0.7" />
      {nodes.map((n) => (
        <g key={n.l + n.cy}>
          <circle cx={n.cx} cy={n.cy} r="22" stroke="var(--line)" strokeWidth="1" fill="var(--surface)" />
          <circle cx={n.cx} cy={n.cy} r="22" stroke="var(--pop)" strokeWidth="1.5" strokeDasharray="4 6" className="animate-dash" opacity="0.5" />
          <circle cx={n.cx} cy={n.cy} r="6" fill="var(--pop)" />
          <text x={n.cx} y={n.cy + 44} textAnchor="middle" fill="var(--faint)" fontSize="9" letterSpacing="3">{n.l}</text>
        </g>
      ))}
    </svg>
    <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3.6, ease: "easeInOut" }} className={`${chipCls} right-4 top-5 max-sm:hidden`}>
      <Moon size={14} className="text-pop" />
      <span className={chipText}>2:00 AM — Still Working</span>
    </motion.div>
  </div>
);

const bars = [0.42, 0.68, 0.5, 0.85, 0.6, 1, 0.78];

export const DashboardVisual = () => (
  <div className="visual-grid relative flex h-full w-full flex-col justify-end p-8">
    <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }} className={`${chipCls} left-6 top-6`}>
      <TrendingUp size={14} className="text-pop" />
      <span className={chipText}>+38% Efficiency</span>
    </motion.div>
    <div className="flex h-3/5 items-end gap-3">
      {bars.map((b, i) => (
        <motion.div
          key={i}
          animate={{ scaleY: [0.25, 1, 0.6, 1] }}
          transition={{ repeat: Infinity, duration: 3 + i * 0.35, ease: "easeInOut", delay: i * 0.15 }}
          style={{ height: `${b * 100}%`, transformOrigin: "bottom" }}
          className={`w-full ${i === bars.length - 2 ? "bg-pop" : "bg-myrtle/45"}`}
        />
      ))}
    </div>
    <div className="mt-3 h-px w-full bg-line" />
    <div className="mt-3 flex justify-between text-[9px] uppercase tracking-[0.2em] text-faint">
      <span>Mon</span><span>Wed</span><span>Fri</span><span>Sun</span>
    </div>
  </div>
);
